import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { useAuth } from '../../context/AuthContext';
import { formatRoleLabel } from '../../config/appRoutes';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import { useMultiFetch } from '../../hooks/useMultiFetch';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DataTable from '../ui/DataTable';
import { useConfirm } from '../ui/ConfirmDialog';
import DirectorPanelState from './DirectorPanelState';
import DirectorModal from './DirectorModal';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../../utils/directorEvents';

const ROLE_OPTIONS = [
  { value: 'employee', label: formatRoleLabel('employee') },
  { value: 'supervisor', label: formatRoleLabel('supervisor') },
  { value: 'ops_manager', label: formatRoleLabel('ops_manager') },
  { value: 'airport_manager', label: formatRoleLabel('airport_manager') },
  { value: 'director', label: formatRoleLabel('director') },
  { value: 'admin', label: formatRoleLabel('admin') }
];

const PERMISSION_OPTIONS = [
  { value: 'access_all_locations', label: 'Access All Locations' },
  { value: 'manage_kpis', label: 'Manage KPIs' },
  { value: 'manage_locations', label: 'Manage Locations' },
  { value: 'manage_users', label: 'Manage Users' },
  { value: 'manage_goals', label: 'Manage Goals' },
  { value: 'manage_policies', label: 'Manage Policies' },
  { value: 'manage_permissions', label: 'Manage Permissions' },
  { value: 'update_all_scorecards', label: 'Update All Scorecards' },
  { value: 'view_peer_airport_managers', label: 'View Peer Location Managers' },
  { value: 'view_station_summary', label: 'View Location Summary' },
  { value: 'view_own_scorecard', label: 'View Own Scorecard' },
  { value: 'submit_vacation_request', label: 'Submit Vacation Requests' },
  { value: 'view_own_request_history', label: 'View Own Request History' },
  { value: 'view_station_ops_managers', label: 'View Location Managers' },
  { value: 'update_station_ops_scorecards', label: 'Update Location Ops Scorecards' },
  { value: 'view_all_locations', label: 'View All Locations' },
  { value: 'view_all_airport_managers', label: 'View All Location Managers' },
  { value: 'view_all_ops_managers', label: 'View All Managers' }
];

const ALL_DIRECTOR_LOCATIONS = 'ALL';
const EMPTY_USER_FORM = {
  username: '',
  email: '',
  password: '',
  employeeProfileId: '',
  role: 'employee',
  assignedLocationIds: [],
  permissionKeys: []
};
const EMPTY_PROFILE_DRAFT = {
  email: '',
  role: 'employee',
  employeeProfileId: '',
  assignedLocationIds: []
};

function summarizeAssignedLocations(user) {
  const locationCodes = Array.from(new Set([
    user?.locationCode || null,
    ...((user?.assignedLocations || []).map(location => location.locationCode || null))
  ].filter(Boolean)));
  if (!locationCodes.length) {
    return '';
  }

  return locationCodes.join(', ');
}

function formatDateTime(iso) {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function normalizeProfileDisplayName(profile) {
  return profile?.preferredName
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
    || profile?.managerName
    || (profile?.id ? `Profile #${profile.id}` : 'Unnamed employee');
}

function profileLocationCode(profile, locationsById) {
  const fromManager = profile?.managerLocationCode || null;
  if (fromManager) return fromManager;
  const fromLocation = locationsById.get(Number(profile?.locationId || 0));
  return fromLocation?.code || null;
}

function buildProfileOption(profile, locationsById) {
  const displayName = normalizeProfileDisplayName(profile);
  const locationCode = profileLocationCode(profile, locationsById);
  const managerLabel = profile?.managerId
    ? [profile?.managerFirstName, profile?.managerLastName].filter(Boolean).join(' ').trim() || `Roster record #${profile.managerId}`
    : null;

  const suffix = [
    locationCode ? `@ ${locationCode}` : null,
    managerLabel ? `Roster-linked: ${managerLabel}` : 'Standalone'
  ].filter(Boolean).join(' · ');

  return {
    value: String(profile.id),
    label: suffix ? `${displayName} (${suffix})` : displayName
  };
}

function matchesSelectedLocationCode(profile, selectedLocationCode, locationsById) {
  if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
    return true;
  }
  return profileLocationCode(profile, locationsById) === selectedLocationCode;
}

async function loadAllEmployeeProfiles() {
  const items = [];
  let nextCursor = null;

  do {
    const params = new URLSearchParams({ limit: '100' });
    if (nextCursor) {
      params.set('cursor', nextCursor);
    }
    const response = await apiRequest(`/api/employees/profiles?${params.toString()}`);
    items.push(...(Array.isArray(response?.items) ? response.items : []));
    nextCursor = response?.nextCursor || null;
  } while (nextCursor);

  return items;
}

function DirectorUsersPanel({ selectedLocationCode = ALL_DIRECTOR_LOCATIONS, locationLabel = 'Company Rollup' }) {
  const confirm = useConfirm();
  const { user: authUser } = useAuth();
  const [form, setForm] = useState(EMPTY_USER_FORM);
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState('');
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [linkFilter, setLinkFilter] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [editingPermissionsUserId, setEditingPermissionsUserId] = useState(null);
  const [permissionDraft, setPermissionDraft] = useState({});
  const [editingProfileUserId, setEditingProfileUserId] = useState(null);
  const [profileDraft, setProfileDraft] = useState(EMPTY_PROFILE_DRAFT);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');

  const canManagePermissions = Boolean(authUser?.permissions?.includes('manage_permissions'));
  const userQuery = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
    ? `?locationCode=${encodeURIComponent(selectedLocationCode)}`
    : '';

  const { data: fetchedAccess, isLoading, error: fetchError, refetch } = useMultiFetch({
    users: `/api/users${userQuery}`,
    locations: '/api/locations',
  }, { deps: [selectedLocationCode] });

  useEffect(() => {
    if (fetchedAccess.users) setUsers(fetchedAccess.users);
    if (fetchedAccess.locations) setLocations(fetchedAccess.locations);
    if (fetchError) {
      setError(fetchError);
    } else if (fetchedAccess.users) {
      setError('');
    }
  }, [fetchedAccess, fetchError]);

  useEffect(() => {
    let cancelled = false;

    async function fetchProfiles() {
      try {
        const items = await loadAllEmployeeProfiles();
        if (cancelled) return;
        setProfiles(items);
        setProfilesLoaded(true);
        setProfilesError('');
      } catch (requestError) {
        if (cancelled) return;
        setProfiles([]);
        setProfilesLoaded(true);
        setProfilesError(requestError?.message || 'Unable to load employee profiles.');
      }
    }

    fetchProfiles();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  const locationsById = useMemo(
    () => new Map((locations || []).map(location => [Number(location.id), location])),
    [locations]
  );

  const visibleProfiles = useMemo(
    () => profiles.filter(profile => matchesSelectedLocationCode(profile, selectedLocationCode, locationsById)),
    [locationsById, profiles, selectedLocationCode]
  );

  const profileOptions = useMemo(
    () => visibleProfiles.map(profile => buildProfileOption(profile, locationsById)),
    [locationsById, visibleProfiles]
  );

  const filteredUsers = useMemo(() => {
    if (linkFilter === 'linked') {
      return users.filter(user => Boolean(user.employeeProfileId));
    }
    if (linkFilter === 'unlinked') {
      return users.filter(user => !user.employeeProfileId);
    }
    return users;
  }, [linkFilter, users]);

  const activeUserCount = useMemo(
    () => users.filter(user => user.isActive).length,
    [users]
  );

  const linkedUserCount = useMemo(
    () => users.filter(user => user.employeeProfileId).length,
    [users]
  );

  const locationOptions = useMemo(
    () => locations
      .filter(location => location.isActive)
      .map(location => ({
        value: String(location.id),
        label: `${location.code} - ${location.name}`
      })),
    [locations]
  );

  const handlePermissionToggle = (permissionKey) => {
    setForm(current => ({
      ...current,
      permissionKeys: current.permissionKeys.includes(permissionKey)
        ? current.permissionKeys.filter(value => value !== permissionKey)
        : [...current.permissionKeys, permissionKey]
    }));
  };

  const handlePermissionEditorToggle = (user) => {
    if (editingPermissionsUserId === user.id) {
      setEditingPermissionsUserId(null);
      setPermissionDraft({});
      return;
    }

    setEditingProfileUserId(null);
    setProfileDraft(EMPTY_PROFILE_DRAFT);
    setEditingPermissionsUserId(user.id);
    setPermissionDraft(Object.fromEntries(
      (user.permissionOverrides || []).map(override => [override.permissionKey, override.state])
    ));
  };

  const handleProfileEditorToggle = (user) => {
    if (editingProfileUserId === user.id) {
      setEditingProfileUserId(null);
      setProfileDraft(EMPTY_PROFILE_DRAFT);
      return;
    }

    setEditingPermissionsUserId(null);
    setPermissionDraft({});
    setEditingProfileUserId(user.id);
    setProfileDraft({
      email: user.email || '',
      role: user.role || 'employee',
      employeeProfileId: user.employeeProfileId ? String(user.employeeProfileId) : '',
      assignedLocationIds: (user.assignedLocationIds || []).map(id => String(id))
    });
  };

  const handleDraftPermissionChange = (permissionKey, state) => {
    setPermissionDraft(current => {
      const next = { ...current };
      if (state === 'inherit') {
        delete next[permissionKey];
      } else {
        next[permissionKey] = state;
      }
      return next;
    });
  };

  const toggleFormAssignedLocation = (locationId) => {
    setForm(current => ({
      ...current,
      assignedLocationIds: current.assignedLocationIds.includes(locationId)
        ? current.assignedLocationIds.filter(value => value !== locationId)
        : [...current.assignedLocationIds, locationId]
    }));
  };

  const toggleDraftAssignedLocation = (locationId) => {
    setProfileDraft(current => ({
      ...current,
      assignedLocationIds: current.assignedLocationIds.includes(locationId)
        ? current.assignedLocationIds.filter(value => value !== locationId)
        : [...current.assignedLocationIds, locationId]
    }));
  };

  const handleSavePermissions = async (user) => {
    setIsSavingPermissions(true);
    setError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          permissionOverrides: Object.entries(permissionDraft).map(([permissionKey, state]) => ({
            permissionKey,
            state
          }))
        })
      });

      setUsers(current => current.map(item => (item.id === updated.id ? updated : item)));
      setEditingPermissionsUserId(null);
      setPermissionDraft({});
      setBanner(`Permissions updated for ${updated.email}.`);
      notifyDirectorAdminToast(`Permissions updated for ${updated.email}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for user permission changes.');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to update permissions.', 'error');
      setError(requestError.message || 'Unable to update permissions.');
    } finally {
      setIsSavingPermissions(false);
    }
  };

  const handleSaveProfile = async (user) => {
    setIsSavingProfile(true);
    setError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          email: profileDraft.email,
          role: profileDraft.role,
          employeeProfileId: profileDraft.employeeProfileId ? Number(profileDraft.employeeProfileId) : null,
          assignedLocationIds: canManagePermissions
            ? profileDraft.assignedLocationIds.map(value => Number(value))
            : undefined
        })
      });

      setUsers(current => current.map(item => (item.id === updated.id ? updated : item)));
      setEditingProfileUserId(null);
      setProfileDraft(EMPTY_PROFILE_DRAFT);
      setBanner(
        updated.employeeProfileId
          ? `Linked ${updated.email} to ${updated.displayName || `profile #${updated.employeeProfileId}`}.`
          : `Updated account for ${updated.email}.`
      );
      notifyDirectorAdminToast(
        updated.employeeProfileId
          ? `Linked ${updated.email} to ${updated.displayName || `profile #${updated.employeeProfileId}`}.`
          : `Updated account for ${updated.email}.`
      );
      notifyDirectorDataUpdated('Director dashboard refreshed for user profile changes.');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to update the user profile.', 'error');
      setError(requestError.message || 'Unable to update the user profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    setBanner('');

    try {
      const nextUser = await apiRequest('/api/users', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          employeeProfileId: form.employeeProfileId ? Number(form.employeeProfileId) : null,
          assignedLocationIds: canManagePermissions
            ? form.assignedLocationIds.map(value => Number(value))
            : undefined
        })
      });

      setUsers(current => [...current, nextUser].sort((a, b) => `${a.email}`.localeCompare(`${b.email}`)));
      setForm(EMPTY_USER_FORM);
      setBanner(`Access created for ${nextUser.email}. Passwords are stored hashed and never shown again.`);
      notifyDirectorAdminToast(`User access created for ${nextUser.email}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for user changes.');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to create user.', 'error');
      setError(requestError.message || 'Unable to create user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccessToggle = async (user) => {
    const confirmed = await confirm(
      user.isActive
        ? `Deactivate ${user.email}? They will lose access until reactivated, but their history will remain intact.`
        : `Activate ${user.email}? They will regain access with their current role assignments.`,
      user.isActive
        ? { title: 'Deactivate user', confirmLabel: 'Deactivate', tone: 'danger' }
        : { title: 'Activate user', confirmLabel: 'Activate' }
    );

    if (!confirmed) {
      return;
    }

    setError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/users/${user.id}/access`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !user.isActive })
      });

      setUsers(current => current.map(item => (item.id === updated.id ? updated : item)));
      setBanner(`${updated.email} is now ${updated.isActive ? 'active' : 'inactive'}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for user changes.');
      notifyDirectorAdminToast(`${updated.email} is now ${updated.isActive ? 'active' : 'inactive'}.`, 'success', {
        label: 'Undo',
        onClick: async () => {
          const reverted = await apiRequest(`/api/users/${updated.id}/access`, {
            method: 'PATCH',
            body: JSON.stringify({ isActive: user.isActive })
          });

          setUsers(current => current.map(item => (item.id === reverted.id ? reverted : item)));
          setBanner(`${reverted.email} is now ${reverted.isActive ? 'active' : 'inactive'}.`);
          notifyDirectorDataUpdated('Director dashboard refreshed for user changes.');
          notifyDirectorAdminToast(`${reverted.email} is now ${reverted.isActive ? 'active' : 'inactive'}.`);
        }
      });
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to update access state.', 'error');
      setError(requestError.message || 'Unable to update access state.');
    }
  };

  const handleSelectUserForReset = (user) => {
    setSelectedUserId(current => (current === user.id ? current : user.id));
    setResetPasswordValue('');
    setError('');
  };

  const handleClearSelectedUser = () => {
    setSelectedUserId(null);
    setResetPasswordValue('');
  };

  const handleResetPassword = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    const user = users.find(item => item.id === selectedUserId);
    if (!user) {
      notifyDirectorAdminToast('Select an account before resetting a password.', 'warning');
      setError('Select an account before resetting a password.');
      return;
    }

    if (!resetPasswordValue) {
      notifyDirectorAdminToast('Enter a temporary password before resetting.', 'warning');
      setError('Enter a temporary password before resetting.');
      return;
    }

    const confirmed = await confirm(
      `Reset the password for ${user.email}? Their existing sessions will be signed out and they must set a new password on next sign-in.`,
      { title: 'Reset password', confirmLabel: 'Reset Password', tone: 'danger' }
    );

    if (!confirmed) {
      return;
    }

    setError('');
    setBanner('');
    setIsResettingPassword(true);

    try {
      await apiRequest(`/api/users/${user.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword: resetPasswordValue })
      });

      setBanner(`Password reset for ${user.email}. The temporary password is now active and they will be required to change it on next login.`);
      notifyDirectorAdminToast(`Password reset for ${user.email}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for user changes.');
      setResetPasswordValue('');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to reset password.', 'error');
      setError(requestError.message || 'Unable to reset password.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const profileLookup = useMemo(
    () => new Map(profiles.map(profile => [Number(profile.id), profile])),
    [profiles]
  );

  const selectedUser = useMemo(
    () => users.find(user => user.id === selectedUserId) || null,
    [users, selectedUserId]
  );

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading || !profilesLoaded ? 'Loading user access data...' : ''} />
      <DirectorPanelState kind="error" message={error || profilesError} />
      <DirectorPanelState kind="success" message={!error && !profilesError ? banner : ''} />

      <div className="director-split-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>Security & Access Administration</h2>
            <span>{users.length} accounts</span>
          </div>
          <p className="role-section-copy">
            Manage sign-in, role assignment, password resets, and account status for people in {locationLabel}. Employee Profile is now the primary account link; manager data remains compatibility-only when the person is manager-backed.
          </p>

          <form className="director-form-grid" onSubmit={handleCreateUser}>
            <label>
              Employee Profile
              <select
                value={form.employeeProfileId}
                onChange={(event) => setForm(current => ({ ...current, employeeProfileId: event.target.value }))}
              >
                <option value="">No employee profile linked</option>
                {profileOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Username
              <input value={form.username} onChange={(event) => setForm(current => ({ ...current, username: event.target.value }))} />
            </label>
            <label>
              Email
              <input type="email" value={form.email} onChange={(event) => setForm(current => ({ ...current, email: event.target.value }))} required />
            </label>
            <label>
              Temporary Password
              <input type="password" value={form.password} onChange={(event) => setForm(current => ({ ...current, password: event.target.value }))} required />
            </label>
            <label>
              Role
              <select value={form.role} onChange={(event) => setForm(current => ({ ...current, role: event.target.value }))}>
                {ROLE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {canManagePermissions ? (
              <div className="director-permissions director-form-span">
                <span>Assigned Locations</span>
                <p className="director-muted">
                  Select one or more locations for scoped access. Manager-backed users still keep their linked manager location as a compatibility fallback.
                </p>
                <div className="director-checkbox-grid">
                  {locationOptions.map(option => (
                    <label key={option.value} className="director-checkbox">
                      <input
                        type="checkbox"
                        checked={form.assignedLocationIds.includes(option.value)}
                        onChange={() => toggleFormAssignedLocation(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            {canManagePermissions ? (
              <div className="director-permissions">
                <span>Permissions</span>
                <div className="director-checkbox-grid">
                  {PERMISSION_OPTIONS.map(option => (
                    <label key={option.value} className="director-checkbox">
                      <input
                        type="checkbox"
                        checked={form.permissionKeys.includes(option.value)}
                        onChange={() => handlePermissionToggle(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="director-form-actions">
              <Button type="submit" loading={isSubmitting} loadingLabel="Saving…">
                Create Account
              </Button>
            </div>
          </form>
        </section>

        <section className="director-card director-note-card">
          <div className="role-section-header">
            <h2>Password Reset</h2>
            <span>User admins</span>
          </div>
          <p className="role-section-copy">
            Use temporary passwords for resets. Users are forced to change them on next sign-in.
          </p>
          {selectedUser ? (
            <form className="director-reset-panel" onSubmit={handleResetPassword}>
              <div className="director-reset-panel-target">
                <span className="director-reset-panel-label">Selected account</span>
                <strong>{selectedUser.displayName || selectedUser.managerName || selectedUser.email}</strong>
                <span className="director-muted">{selectedUser.email} · {formatRoleLabel(selectedUser.role)}</span>
              </div>
              <label>
                Temporary Password
                <input
                  type="password"
                  value={resetPasswordValue}
                  onChange={(event) => setResetPasswordValue(event.target.value)}
                  placeholder="Temporary password"
                  aria-label={`Temporary password for ${selectedUser.email}`}
                />
              </label>
              <div className="director-form-actions">
                <Button type="submit" variant="secondary" loading={isResettingPassword} loadingLabel="Resetting…">
                  Reset Password
                </Button>
                <Button type="button" variant="ghost" onClick={handleClearSelectedUser}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="director-muted director-reset-panel-empty">
              Select an account from the User Access Directory below to reset its password.
            </p>
          )}
        </section>
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>User Access Directory</h2>
          <div className="role-section-actions">
            <span>{activeUserCount} active</span>
            <span>{linkedUserCount} profile-linked</span>
          </div>
        </div>
        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <span>Profile Link</span>
            <div className="director-filter-chip-row">
              <button
                type="button"
                className={`director-filter-chip ${linkFilter === 'all' ? 'active' : ''}`}
                onClick={() => setLinkFilter('all')}
              >
                All
              </button>
              <button
                type="button"
                className={`director-filter-chip ${linkFilter === 'linked' ? 'active' : ''}`}
                onClick={() => setLinkFilter('linked')}
              >
                Linked
              </button>
              <button
                type="button"
                className={`director-filter-chip ${linkFilter === 'unlinked' ? 'active' : ''}`}
                onClick={() => setLinkFilter('unlinked')}
              >
                Unlinked
              </button>
            </div>
          </div>
        </div>
        <DataTable
          tableClassName="director-users-table"
          emptyMessage="No user accounts match the current location scope and profile-link filter."
          isEmpty={!filteredUsers.length}
          columns={[
            { label: 'Employee Name' },
            { label: 'Email', className: 'director-users-table-col-email' },
            { label: 'Role', className: 'director-users-table-col-role' },
            { label: 'Status', className: 'director-users-table-col-status' },
            { label: 'Employee Profile', className: 'director-users-table-col-linked' },
            { label: 'Roster Link', className: 'director-users-table-col-station' },
            { label: 'Last Login', className: 'director-users-table-col-status' },
            { label: 'Account Enabled', className: 'director-users-table-col-status' },
            { label: 'Permissions', className: 'director-users-table-col-permissions' },
            { label: 'Reset Password', className: 'director-users-table-col-reset' },
            { label: 'Actions', className: 'director-users-table-col-actions' }
          ]}
        >
          {filteredUsers.map(user => {
            const linkedProfile = user.employeeProfileId
              ? profileLookup.get(Number(user.employeeProfileId))
              : null;
            const profileLink = buildPersonWorkspacePath({
              employeeProfileId: user.employeeProfileId,
              managerId: user.managerId
            });

            const isSelected = selectedUserId === user.id;

            return (
                <tr
                  key={user.id}
                  className={isSelected ? 'director-users-table-row-selected' : undefined}
                  aria-selected={isSelected}
                  onClick={() => handleSelectUserForReset(user)}
                >
                  <td>{user.displayName || user.managerName || user.email}</td>
                  <td className="director-users-table-col-email">{user.email}</td>
                  <td className="director-users-table-col-role">{formatRoleLabel(user.role)}</td>
                  <td className="director-users-table-col-status">
                    <StatusPill tone={user.employeeProfileId ? 'linked' : 'needs-link'}>
                      {user.employeeProfileId ? 'Linked' : 'Unlinked'}
                    </StatusPill>
                  </td>
                  <td className="director-users-table-col-linked">
                    {user.employeeProfileId ? (
                      <>
                        {user.displayName || linkedProfile?.preferredName || `Profile #${user.employeeProfileId}`}
                        {' · '}
                        <a
                          href={profileLink}
                          className="director-users-profile-link"
                          data-testid={`user-open-profile-${user.id}`}
                        >
                          Open workspace
                        </a>
                      </>
                    ) : 'No employee profile linked'}
                  </td>
                  <td className="director-users-table-col-station">
                    {user.managerName || user.managerKey || (user.managerId ? `Roster record #${user.managerId}` : '—')}
                  </td>
                  <td className="director-users-table-col-status">{formatDateTime(user.lastLoginAt)}</td>
                  <td className="director-users-table-col-status">
                    <StatusPill tone={user.isActive ? 'active' : 'inactive'}>
                      {user.isActive ? 'Enabled' : 'Disabled'}
                    </StatusPill>
                  </td>
                  <td className="director-users-table-col-permissions">
                    <div>{summarizeAssignedLocations(user) || 'No scoped locations'}</div>
                    {user.permissionKeys?.length ? (
                      <p className="director-muted">
                        {user.permissionKeys.length} permission{user.permissionKeys.length === 1 ? '' : 's'}
                      </p>
                    ) : null}
                  </td>
                  <td className="director-users-table-col-reset">
                    <Button
                      size="sm"
                      variant={isSelected ? 'primary' : 'secondary'}
                      aria-pressed={isSelected}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectUserForReset(user);
                      }}
                    >
                      {isSelected ? 'Selected' : 'Select for Reset'}
                    </Button>
                  </td>
                  <td className="director-users-table-col-actions">
                    <div className="director-table-actions">
                      {canManagePermissions ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePermissionEditorToggle(user);
                          }}
                        >
                          Edit Permissions
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleProfileEditorToggle(user);
                        }}
                      >
                        Edit Account
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant={user.isActive ? 'danger' : 'secondary'}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleAccessToggle(user);
                      }}
                    >
                      {user.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  </td>
                </tr>
            );
          })}
        </DataTable>
      </section>

      {editingProfileUserId ? (
        <DirectorModal
          title="Edit Account"
          badge="User Access"
          onClose={() => handleProfileEditorToggle({ id: editingProfileUserId })}
        >
          {(() => {
            const user = users.find(item => item.id === editingProfileUserId);
            if (!user) {
              return null;
            }

            return (
              <>
                <div className="director-history-row">
                  <strong>{user.displayName || user.email}</strong>
                  <span>{formatRoleLabel(user.role)}</span>
                </div>
                <p className="director-muted">
                  Update the email, role, or linked employee profile for this account.
                </p>
                <div className="director-form-grid director-inline-form-grid">
                  <label>
                    Email
                    <input
                      type="email"
                      value={profileDraft.email}
                      onChange={(event) => setProfileDraft(current => ({ ...current, email: event.target.value }))}
                    />
                  </label>
                  <label>
                    Role
                    <select
                      value={profileDraft.role}
                      onChange={(event) => setProfileDraft(current => ({ ...current, role: event.target.value }))}
                    >
                      {ROLE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="director-form-span">
                    Employee Profile
                    <select
                      value={profileDraft.employeeProfileId}
                      onChange={(event) => setProfileDraft(current => ({ ...current, employeeProfileId: event.target.value }))}
                    >
                      <option value="">No employee profile linked</option>
                      {profileOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  {canManagePermissions ? (
                    <div className="director-permissions director-form-span">
                      <span>Assigned Locations</span>
                      <p className="director-muted">
                        Duplicate assignments are ignored automatically. Leave this empty only when the user has all-location access or the linked employee already provides the correct operational context.
                      </p>
                      <div className="director-checkbox-grid">
                        {locationOptions.map(option => (
                          <label key={option.value} className="director-checkbox">
                            <input
                              type="checkbox"
                              checked={profileDraft.assignedLocationIds.includes(option.value)}
                              onChange={() => toggleDraftAssignedLocation(option.value)}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="director-form-actions">
                  <Button
                    onClick={() => handleSaveProfile(user)}
                    loading={isSavingProfile}
                    loadingLabel="Saving…"
                  >
                    Save Account
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleProfileEditorToggle(user)}
                    disabled={isSavingProfile}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            );
          })()}
        </DirectorModal>
      ) : null}

      {canManagePermissions && editingPermissionsUserId ? (
        <DirectorModal
          title="Edit Permissions"
          badge="User Access"
          onClose={() => handlePermissionEditorToggle({ id: editingPermissionsUserId })}
        >
          {(() => {
            const user = users.find(item => item.id === editingPermissionsUserId);
            if (!user) {
              return null;
            }

            return (
              <>
                <div className="director-history-row">
                  <strong>{user.email}</strong>
                  <span>{formatRoleLabel(user.role)}</span>
                </div>
                <p className="director-muted">
                  Choose whether each permission is inherited from the role, explicitly allowed, or explicitly denied.
                </p>
                <div className="director-checkbox-grid">
                  {PERMISSION_OPTIONS.map(option => (
                    <label key={option.value} className="director-checkbox">
                      <span>{option.label}</span>
                      <select
                        aria-label={`${option.label} permission override`}
                        value={permissionDraft[option.value] || 'inherit'}
                        onChange={event => handleDraftPermissionChange(option.value, event.target.value)}
                      >
                        <option value="inherit">
                          Inherit ({(user.inheritedPermissionKeys || []).includes(option.value) ? 'allowed' : 'not granted'})
                        </option>
                        <option value="allow">Explicitly allow</option>
                        <option value="deny">Explicitly deny</option>
                      </select>
                    </label>
                  ))}
                </div>
                <div className="director-form-actions">
                  <Button
                    onClick={() => handleSavePermissions(user)}
                    loading={isSavingPermissions}
                    loadingLabel="Saving…"
                  >
                    Save Permissions
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handlePermissionEditorToggle(user)}
                    disabled={isSavingPermissions}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            );
          })()}
        </DirectorModal>
      ) : null}
    </div>
  );
}

export default DirectorUsersPanel;
